'use strict';
/* ==========================================================================
   core-local/plate.js — the lesson's look: a nursery greenhouse in antique BOTANICAL-PLATE collage (DESIGN.md §2),
   drawn through the collage engine. Every piece is a cached shape (paperRect / paperCircle / paperPiece with fixed
   seeds, sizes rounded to whole px and fixed point lists, baked once into sprites by the engine); animation is by
   transform (translate / rotate / scale), never a new shape per frame, and no shadowOn(): paper brings its own
   pre-baked shadow.

   Plate.C                         colors (kraft, cream, ink, leaf, tile greens, wood, pea yellow / green, violet …)
   Plate.woodtype(str, x, y, size, o)   woodtype letters, one cream block each {color, ink, t0, seed, align, gap, stagger,
                                   rot} → total width (letters pop in from t0)
   Plate.label(str, x, y, size, o) a kraft seed-packet label: a kraft strip, twine holes, woodtype on it
                                   {t0, seed, rot, color, block, ink, s} → {w, h}
   Plate.twine(x0, y0, x1, y1, o)  a sagging two-ply garden twine {sag, seed, w}
   Plate.tile(letter, x, y, size, o)    an ALLELE TILE: a capital letter on a CREAM tile (dark ink), a small letter on a
                                   GREEN tile (cream ink). Color is extra; the case carries the meaning (color-blind
                                   safe). {s, rot, seed, alpha, state: null | 'hi' | 'ok' | 'bad' | 'ghost'}
   Plate.trayLayout(x, y, cell)    the Punnett tray's geometry (x, y = the centre of the 2 × 2 grid): {cell, x, y, w, h,
                                   boxes: [4 × {i, x, y, w, h}] row-major (top-left, top-right, bottom-left, bottom-right),
                                   top: [2 × {x, y}] (parent 1's edge slots), side: [2 × {x, y}] (parent 2's), tileSize}
   Plate.tray(x, y, cell, o)       the 2 × 2 WOODEN SEED TRAY Punnett square: {top: ['Y','y'] | null, side, boxes: [4 ×
                                   genotype | null], trait (a Cross.TRAITS key: each filled box gets its phenotype picture),
                                   hi: box index, bad: box index (red pencil ring), ok: [box indices], seed, s, t0 (boxes
                                   pop in one by one from t0), label (a kraft label above), pics: true}
                                   → the layout (hit-test the boxes / edge slots with it)
   Plate.pheno(trait, value, x, y, r, o)  the phenotype picture, drawn simply in ink + paper: pea (yellow / green),
                                   peashape (round / wrinkled), flower (violet / white), pod (inflated / constricted),
                                   stem (tall / short), ear (curled / straight: the made-up cat), eye (brown / blue)
                                   `trait` may be a TRAITS key or a pic kind; `value` a phenotype word {seed, s}
   Plate.packet(x, y, w, h, o)     a SEED PACKET: kraft body, folded flap, a cream label with a number seal, the title
                                   in slab type, a picture window {title, sub, n, color, seed, rot, s, hover, t0, pic:
                                   {trait, value} | {img}}
   Plate.greenhouse(x, y, w, h, o) the greenhouse: IMG.greenhouse when the art is in, else glass panes on a white frame
                                   and a gable roof {seed, t0}
   Plate.vine(x, y, h, o)          a climbing pea vine: IMG.pea_plant when the art is in, else an ink stem with paper
                                   leaves, tendrils and pods {seed, side (+1 leans right / −1 left), sway, s}
   Plate.backdrop(t0)              the shared scene backdrop: aged paper, a botanical-plate border
   Plate.seal(x, y, r, o)          a green wax seal with a number {text, seed, s, rot, color}
   ========================================================================== */
(function () {
  const C = {
    paper: '#efe4c8', cream: '#f6ecd2', ink: '#2b2420', kraft: '#c99a67', kraftDark: '#a47444', label: '#f3e6c4',
    leaf: '#5b8a3c', leafDark: '#2f5226', tileGreen: '#3f7040', tileCream: '#f8efd6', wood: '#9c6b3c', woodDark: '#6a4322',
    woodLight: '#bf8d58', soil: '#4a3526', twine: '#b58a55', red: '#b83a2b', yellowPea: '#e9c53d', greenPea: '#7fae4b',
    violet: '#7a4c9c', white: '#fbf8ef', brownEye: '#6b4424', blueEye: '#4f86b8', glass: '#cfe3dc', frame: '#f4f1e8',
    peaNeutral: '#d8c66a', catFur: '#e8d3b0',
  };
  const SLAB = "'Alfa Slab One'", PLATE_FONT = "'Playfair Display'";

  /* fixed point lists (built once, so the engine caches their sprites) */
  const _pts = {};
  const pts = (key, make) => _pts[key] || (_pts[key] = Object.assign(make(), { k: 'plate:' + key }));
  const R = v => Math.max(1, Math.round(v));

  /* ---------------- woodtype + labels ---------------- */
  function woodtype(str, x, y, size, { color = C.cream, ink: inkC = C.ink, t0 = -1e6, seed = 1, align = 'center', gap = 0.06, stagger = 0.04, rot = 0.03 } = {}) {
    const chars = [...String(str)];
    g.save(); g.font = `${size}px ${SLAB}`;
    const widths = chars.map(ch => (ch === ' ' ? size * 0.34 : g.measureText(ch).width + size * 0.2));
    g.restore();
    const total = widths.reduce((a, w) => a + w, 0) + gap * size * (chars.length - 1);
    let cx = align === 'center' ? x - total / 2 : align === 'right' ? x - total : x;
    const r = rng(seed);
    chars.forEach((ch, i) => {
      const w = widths[i], px = cx + w / 2, jr = (r() - 0.5) * 2 * rot, jy = (r() - 0.5) * size * 0.05;
      cx += w + gap * size;
      if (ch === ' ') return;
      const s = pop(T, t0 + i * stagger, 0.3, 2.2);
      if (s <= 0) return;
      g.save(); g.translate(px, y + jy); g.rotate(jr); g.scale(s, s);
      paperRect(0, 0, R(w), R(size * 1.1), color, { seed: seed * 100 + i, depth: 0.5, boil: false });
      g.font = `${size}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = inkC;
      g.fillText(ch, 0, size * 0.05);
      g.restore();
    });
    return total;
  }
  const woodWidth = (str, size, gap = 0.06) => {
    const chars = [...String(str)];
    g.save(); g.font = `${size}px ${SLAB}`;
    const w = chars.reduce((a, ch) => a + (ch === ' ' ? size * 0.34 : g.measureText(ch).width + size * 0.2), 0) + gap * size * (chars.length - 1);
    g.restore(); return w;
  };
  function label(str, x, y, size, { t0 = -1e6, seed = 1, rot = 0, color = C.kraft, block = C.label, ink: inkC = C.ink, s = 1 } = {}) {
    const w = woodWidth(str, size) + size * 1.4, h = size * 1.7;
    if (s <= 0.001) return { w, h };
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperRect(0, 0, R(w), R(h), color, { seed, depth: 1.1, boil: false });
    // twine holes (punched, ringed) at both ends
    for (const sx of [-1, 1]) {
      paperCircle(sx * (w / 2 - size * 0.32), 0, R(size * 0.13), C.cream, { seed: seed + (sx > 0 ? 3 : 2), depth: 0, rough: 0.02 });
      inkCircle(sx * (w / 2 - size * 0.32), 0, size * 0.1, { w: 2, color: 'rgba(60,40,20,0.7)', seed: seed + 5, passes: 1 });
    }
    woodtype(str, 0, 0, size, { color: block, ink: inkC, t0, seed: seed + 11, rot: 0.025 });
    g.restore();
    return { w, h };
  }
  function twine(x0, y0, x1, y1, { sag = 30, seed = 1, w = 3 } = {}) {
    const line = [];
    for (let i = 0; i <= 20; i++) { const u = i / 20; line.push([x0 + (x1 - x0) * u, y0 + (y1 - y0) * u + Math.sin(u * Math.PI) * sag]); }
    ink(line, { w, color: C.twine, seed, passes: 1, step: 30, amp: 0.5 });
    ink(line.map(([a, b]) => [a, b + w * 0.5]), { w: w * 0.5, color: 'rgba(90,60,30,0.6)', seed: seed + 1, passes: 1, step: 30, amp: 0.6 });
  }
  function seal(x, y, r, { text = '', seed = 3, s = 1, rot = 0, color = C.leafDark } = {}) {
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    paperPiece(blobPts(R(r), R(r), seed + 5, 0.1, 28), 0, 0, color, { seed: seed + 5, depth: 0.8 });
    inkCircle(0, 0, r * 0.7, { w: 2.5, color: 'rgba(250,240,210,0.55)', seed: seed + 7, passes: 1 });
    if (text) {
      g.font = `${Math.round(r * (text.length > 1 ? 0.8 : 1))}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle';
      g.fillStyle = C.cream; g.fillText(text, 0, r * 0.04);
    }
    g.restore();
  }

  /* ---------------- allele tiles ---------------- */
  function tile(letter, x, y, size, { s = 1, rot = 0, seed = 1, alpha = 1, state = null } = {}) {
    if (s <= 0.001) return;
    const cap = letter === String(letter).toUpperCase(), sz = R(size);
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s);
    if (state === 'ghost') {                        // an empty slot: a dashed pencil outline
      g.globalAlpha *= 0.55;
      for (let k = 0; k < 4; k++) {
        const a = [[-1, -1], [1, -1], [1, 1], [-1, 1]][k], b = [[-1, -1], [1, -1], [1, 1], [-1, 1]][(k + 1) % 4];
        for (let u = 0; u < 1; u += 0.25) ink([[a[0] * sz / 2 + (b[0] - a[0]) * sz / 2 * u, a[1] * sz / 2 + (b[1] - a[1]) * sz / 2 * u], [a[0] * sz / 2 + (b[0] - a[0]) * sz / 2 * (u + 0.14), a[1] * sz / 2 + (b[1] - a[1]) * sz / 2 * (u + 0.14)]], { w: 3, color: C.ink, seed: seed + k * 7 + u * 40, passes: 1, step: 20 });
      }
      g.restore(); return;
    }
    g.globalAlpha *= alpha;
    if (state === 'hi') paperRect(0, 0, R(sz * 1.18), R(sz * 1.18), C.yellowPea, { seed: seed + 50, depth: 0 });
    paperRect(0, 0, sz, sz, cap ? C.tileCream : C.tileGreen, { seed, depth: 0.9, boil: false });
    // a thin inner rule, like a printer's block
    g.strokeStyle = cap ? 'rgba(43,36,32,0.25)' : 'rgba(248,239,214,0.35)'; g.lineWidth = Math.max(1.5, sz * 0.03);
    g.strokeRect(-sz * 0.38, -sz * 0.38, sz * 0.76, sz * 0.76);
    g.font = `${Math.round(sz * (cap ? 0.66 : 0.72))}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle';
    g.fillStyle = cap ? C.ink : C.tileCream;
    g.fillText(letter, 0, cap ? sz * 0.04 : -sz * 0.02);
    if (state === 'ok') ink([[-sz * 0.34, sz * 0.36], [-sz * 0.2, sz * 0.5], [sz * 0.05, sz * 0.26]], { w: 5, color: C.leafDark, seed: seed + 9, passes: 1, step: 10 });
    if (state === 'bad') inkCircle(0, 0, sz * 0.62, { w: 5, color: C.red, seed: seed + 11, passes: 1 });
    g.restore();
  }

  /* ---------------- the seed-tray Punnett square ---------------- */
  function trayLayout(x, y, cell) {
    const tileSize = cell * 0.36, gapEdge = cell * 0.2 + tileSize / 2;
    const boxes = [];
    for (let row = 0; row < 2; row++) for (let col = 0; col < 2; col++) boxes.push({ i: row * 2 + col, row, col, x: x + (col - 0.5) * cell, y: y + (row - 0.5) * cell, w: cell, h: cell });
    const top = [0, 1].map(col => ({ x: x + (col - 0.5) * cell, y: y - cell - gapEdge }));
    const side = [0, 1].map(row => ({ x: x - cell - gapEdge, y: y + (row - 0.5) * cell }));
    return { cell, x, y, w: cell * 2, h: cell * 2, boxes, top, side, tileSize };
  }
  function tray(x, y, cell, { top = null, side = null, boxes = [null, null, null, null], trait = null, hi = -1, bad = -1, ok = [], seed = 31, s = 1, t0 = -1e6, label: lab = null, pics = true, rot = 0 } = {}) {
    const Lz = trayLayout(x, y, cell), rim = R(cell * 0.14), cz = R(cell);
    if (s <= 0.001) return Lz;
    g.save(); g.translate(x, y); g.rotate(rot); g.scale(s, s); g.translate(-x, -y);
    // the wooden tray: an outer frame, planks, the four soil cells
    paperRect(x, y, R(cell * 2 + rim * 2), R(cell * 2 + rim * 2), C.wood, { seed, depth: 1.4, boil: false });
    for (let k = -1; k <= 1; k++) ink([[x - cell - rim * 0.6, y + k * cell * 0.66], [x + cell + rim * 0.6, y + k * cell * 0.66 + 3]], { w: 1.6, color: 'rgba(60,35,15,0.35)', seed: seed + 20 + k, passes: 1, step: 60, amp: 0.8 });
    Lz.boxes.forEach((b, i) => {
      paperRect(b.x, b.y, R(cz - rim * 0.7), R(cz - rim * 0.7), C.soil, { seed: seed + 2 + i, depth: 0, boil: false });
      paperRect(b.x, b.y, R(cz - rim * 1.5), R(cz - rim * 1.5), C.label, { seed: seed + 6 + i, depth: 0.4, boil: false });
    });
    // the parents' edges: little kraft rails the tiles sit on
    paperRect(x, Lz.top[0].y, R(cell * 2), R(Lz.tileSize * 1.35), C.kraft, { seed: seed + 12, depth: 0.5, boil: false });
    paperRect(Lz.side[0].x, y, R(Lz.tileSize * 1.35), R(cell * 2), C.kraft, { seed: seed + 13, depth: 0.5, boil: false });
    for (let k = 0; k < 2; k++) {
      tile(top && top[k] ? top[k] : 'x', Lz.top[k].x, Lz.top[k].y, Lz.tileSize, { seed: seed + 40 + k, state: top && top[k] ? null : 'ghost', rot: (k ? 0.03 : -0.02) });
      tile(side && side[k] ? side[k] : 'x', Lz.side[k].x, Lz.side[k].y, Lz.tileSize, { seed: seed + 44 + k, state: side && side[k] ? null : 'ghost', rot: (k ? -0.025 : 0.02) });
    }
    // each box: two tiles + the phenotype picture
    Lz.boxes.forEach((b, i) => {
      const gtp = boxes[i];
      if (i === hi) paperRect(b.x, b.y, R(cz - rim * 1.5), R(cz - rim * 1.5), '#f7df8e', { seed: seed + 60 + i, depth: 0, boil: false });
      if (!gtp) return;
      const sc = pop(T, t0 + i * 0.12, 0.3, 2.2);
      if (sc <= 0) return;
      g.save(); g.translate(b.x, b.y); g.scale(sc, sc);
      const ts = cell * 0.27, ty = pics && trait ? -cell * 0.17 : 0;
      tile(gtp[0], -ts * 0.56, ty, ts, { seed: seed + 70 + i * 2, rot: -0.03 });
      tile(gtp[1], ts * 0.56, ty, ts, { seed: seed + 71 + i * 2, rot: 0.03 });
      if (pics && trait) {
        const Tr = window.Cross && Cross.TRAITS[trait];
        const word = Tr ? Cross.phenotype(trait, gtp) : null;
        if (word) pheno(trait, word, 0, cell * 0.2, cell * 0.12, { seed: seed + 80 + i });
      }
      g.restore();
      if (ok.includes(i)) ink([[b.x + cell * 0.22, b.y + cell * 0.3], [b.x + cell * 0.29, b.y + cell * 0.38], [b.x + cell * 0.42, b.y + cell * 0.2]], { w: 6, color: C.leafDark, seed: seed + 90 + i, passes: 1, step: 10 });
    });
    if (bad >= 0 && Lz.boxes[bad]) { const b = Lz.boxes[bad]; inkCircle(b.x, b.y, cell * 0.46, { w: 7, color: C.red, seed: seed + 95, passes: 2 }); }
    if (lab) label(lab, x, y - cell - Lz.tileSize * 2.1, Math.round(cell * 0.14), { seed: seed + 99, rot: -0.01 });
    g.restore();
    return Lz;
  }

  /* ---------------- phenotype pictures ---------------- */
  const PIC = { seedColour: 'pea', seedShape: 'peashape', flowerColour: 'flower', podShape: 'pod', stemLength: 'stem', catEar: 'ear', eyeModel: 'eye' };
  function pheno(trait, value, x, y, r, { seed = 5, s = 1 } = {}) {
    const kind = PIC[trait] || trait, rr = R(r);
    if (s <= 0.001) return;
    g.save(); g.translate(x, y); g.scale(s, s);
    const outline = w => ({ w, color: C.ink, seed: seed + 3, passes: 1, step: 12, amp: 0.6 });
    if (kind === 'pea') {
      paperCircle(0, 0, rr, value === 'green' ? C.greenPea : C.yellowPea, { seed, depth: 0.6, rough: 0.03 });
      inkCircle(0, 0, rr * 0.96, { w: 2, color: 'rgba(43,36,32,0.7)', seed: seed + 1, passes: 1 });
      ink([[-rr * 0.45, -rr * 0.2], [-rr * 0.25, -rr * 0.5]], { w: 3, color: 'rgba(255,255,240,0.8)', seed: seed + 2, passes: 1, step: 8 });
    } else if (kind === 'peashape') {
      if (value === 'wrinkled') {
        paperPiece(blobPts(rr, rr, seed + 11, 0.14, 18), 0, 0, C.peaNeutral, { seed: seed + 11, depth: 0.6 });
        // creases running in from the rim (a dry wrinkled pea), not a face
        [[-0.9, -0.35, -0.35, -0.1], [0.15, -0.95, 0.05, -0.35], [0.85, 0.1, 0.3, 0.12], [-0.4, 0.85, -0.12, 0.35], [0.55, 0.7, 0.25, 0.45]]
          .forEach(([a, b, c, d], k) => ink([[a * rr, b * rr], [(a + c) / 2 * rr + rr * 0.08, (b + d) / 2 * rr], [c * rr, d * rr]], { w: 2, color: 'rgba(43,36,32,0.75)', seed: seed + 20 + k, passes: 1, step: 6, amp: 0.4 }));
      } else {
        paperCircle(0, 0, rr, C.peaNeutral, { seed, depth: 0.6, rough: 0.02 });
        inkCircle(0, 0, rr * 0.96, { w: 2, color: 'rgba(43,36,32,0.7)', seed: seed + 1, passes: 1 });
      }
    } else if (kind === 'flower') {
      const col = value === 'white' ? C.white : C.violet;
      for (let k = 0; k < 5; k++) {
        const a = k / 5 * TAU - Math.PI / 2;
        paperCircle(Math.cos(a) * rr * 0.6, Math.sin(a) * rr * 0.6, R(rr * 0.5), col, { seed: seed + k, depth: 0.3, rough: 0.05 });
      }
      if (value === 'white') for (let k = 0; k < 5; k++) { const a = k / 5 * TAU - Math.PI / 2; inkCircle(Math.cos(a) * rr * 0.6, Math.sin(a) * rr * 0.6, rr * 0.48, { w: 1.6, color: 'rgba(43,36,32,0.6)', seed: seed + 20 + k, passes: 1 }); }
      paperCircle(0, 0, R(rr * 0.28), C.yellowPea, { seed: seed + 9, depth: 0 });
    } else if (kind === 'pod') {
      if (value === 'constricted') {
        for (let k = -1; k <= 1; k++) paperCircle(k * rr * 0.62, 0, R(rr * 0.42), C.greenPea, { seed: seed + k + 2, depth: 0.4, rough: 0.04 });
        for (const k of [-0.31, 0.31]) ink([[k * rr, -rr * 0.3], [k * rr, rr * 0.3]], outline(2.5));
      } else {
        paperPiece(pts(`pod${rr}`, () => { const p = []; for (let i = 0; i < 24; i++) { const a = i / 24 * TAU; p.push([Math.cos(a) * rr * 1.25, Math.sin(a) * rr * 0.62]); } return p; }), 0, 0, C.greenPea, { seed, depth: 0.5 });
        ink([[-rr * 1.1, 0], [rr * 1.1, 0]], { w: 1.6, color: 'rgba(43,36,32,0.45)', seed: seed + 4, passes: 1, step: 20 });
      }
      ink([[rr * 1.2, -rr * 0.1], [rr * 1.5, -rr * 0.45]], outline(2.5));   // the stalk
    } else if (kind === 'stem') {
      const top = value === 'tall' ? -rr * 1.25 : -rr * 0.15;
      ink([[0, rr * 1.1], [rr * 0.05, (rr * 1.1 + top) / 2], [0, top]], { w: 4, color: C.leafDark, seed, passes: 1, step: 10 });
      const leaves = value === 'tall' ? [0.75, 0.25, -0.3, -0.85] : [0.75, 0.25];
      leaves.forEach((u, k) => paperPiece(pts(`leaf${rr}`, () => [[0, 0], [rr * 0.5, -rr * 0.18], [rr * 0.62, 0], [rr * 0.5, rr * 0.14]]), 0, u * rr, C.leaf, { seed: seed + k, depth: 0.3, sx: k % 2 ? -1 : 1, rot: k % 2 ? 0.3 : -0.3 }));
      paperRect(0, rr * 1.15, R(rr * 1.1), R(rr * 0.18), C.soil, { seed: seed + 9, depth: 0 });
    } else if (kind === 'ear') {
      // a simple ink cat head: the made-up cat. curled = the ear tips curl back; straight = plain triangles
      paperCircle(0, rr * 0.2, R(rr * 0.8), C.catFur, { seed, depth: 0.4, rough: 0.03 });
      inkCircle(0, rr * 0.2, rr * 0.78, { w: 2, color: 'rgba(43,36,32,0.75)', seed: seed + 1, passes: 1 });
      for (const sx of [-1, 1]) {
        if (value === 'curled') {
          ink([[sx * rr * 0.62, -rr * 0.2], [sx * rr * 0.62, -rr * 0.85], [sx * rr * 0.3, -rr * 0.95], [sx * rr * 0.18, -rr * 0.7], [sx * rr * 0.35, -rr * 0.6]], { w: 3, color: C.ink, seed: seed + 5 + sx, passes: 1, step: 6, amp: 0.4 });
          ink([[sx * rr * 0.62, -rr * 0.2], [sx * rr * 0.2, -rr * 0.5]], { w: 3, color: C.ink, seed: seed + 7 + sx, passes: 1, step: 8, amp: 0.4 });
        } else {
          ink([[sx * rr * 0.68, -rr * 0.15], [sx * rr * 0.55, -rr * 1.05], [sx * rr * 0.18, -rr * 0.5]], { w: 3, color: C.ink, seed: seed + 5 + sx, passes: 1, step: 8, amp: 0.4 });
        }
        paperCircle(sx * rr * 0.3, rr * 0.12, R(rr * 0.08), C.ink, { seed: seed + 9, depth: 0 });
      }
    } else if (kind === 'eye') {
      const almond = pts(`eye${rr}`, () => { const p = []; for (let i = 0; i <= 12; i++) { const u = i / 12; p.push([-rr * 1.2 + u * rr * 2.4, -Math.sin(u * Math.PI) * rr * 0.7]); } for (let i = 1; i < 12; i++) { const u = 1 - i / 12; p.push([-rr * 1.2 + u * rr * 2.4, Math.sin(u * Math.PI) * rr * 0.7]); } return p; });
      paperPiece(almond, 0, 0, C.white, { seed, depth: 0.3 });
      paperCircle(0, 0, R(rr * 0.55), value === 'blue' ? C.blueEye : C.brownEye, { seed: seed + 1, depth: 0, rough: 0.02 });
      paperCircle(0, 0, R(rr * 0.22), C.ink, { seed: seed + 2, depth: 0 });
      ink(almond.slice(0, 13), { w: 2.5, color: C.ink, seed: seed + 3, passes: 1, step: 10, amp: 0.5 });
      ink(almond.slice(12).concat([almond[0]]), { w: 2, color: C.ink, seed: seed + 4, passes: 1, step: 10, amp: 0.5 });
    }
    g.restore();
  }

  /* ---------------- seed packets ---------------- */
  function packet(x, y, w, h, { title = '', sub = '', n = null, color = C.leaf, seed = 1, rot = 0, s = 1, hover = false, t0 = -1e6, pic = null } = {}) {
    if (s <= 0.001) return;
    const W = R(w), H = R(h);
    g.save(); g.translate(x, y + (hover ? -8 : 0)); g.rotate(rot); g.scale(s * (hover ? 1.03 : 1), s * (hover ? 1.03 : 1));
    paperRect(0, 0, W, H, C.kraft, { seed, depth: hover ? 2 : 1.3, boil: false });
    // the folded flap at the top
    paperPiece(pts(`flap${W},${H}`, () => [[-W / 2, -H / 2], [W / 2, -H / 2], [W / 2, -H / 2 + H * 0.1], [0, -H / 2 + H * 0.17], [-W / 2, -H / 2 + H * 0.1]]), 0, 0, C.kraftDark, { seed: seed + 1, depth: 0.5, boil: false });
    // the printed label
    const lw = R(W * 0.86), lh = R(H * 0.62), ly = H * 0.1;
    paperRect(0, ly, lw, lh, C.label, { seed: seed + 2, depth: 0.3, boil: false });
    paperRect(0, ly - lh / 2 + H * 0.04, lw, R(H * 0.07), color, { seed: seed + 3, depth: 0, boil: false });
    g.strokeStyle = 'rgba(43,36,32,0.35)'; g.lineWidth = 2; g.strokeRect(-lw / 2 + 10, ly - lh / 2 + 10, lw - 20, lh - 20);
    // the picture window
    const py = ly - lh * 0.12;
    if (pic && pic.img && IMG[pic.img]) cut(pic.img, 0, py, lh * 0.38, { seed: seed + 4, depth: 0.5, boil: false });
    else if (pic && pic.trait) pheno(pic.trait, pic.value, 0, py, lh * 0.12, { seed: seed + 4 });
    // title (slab, fitted) + sub (hand)
    g.save();
    let fs = Math.round(H * 0.075); g.font = `${fs}px ${SLAB}`;
    const tw = g.measureText(String(title).toUpperCase()).width;
    if (tw > lw * 0.86) fs = Math.floor(fs * lw * 0.86 / tw);
    g.font = `${fs}px ${SLAB}`; g.textAlign = 'center'; g.textBaseline = 'middle'; g.fillStyle = C.ink;
    g.fillText(String(title).toUpperCase(), 0, ly + lh * 0.2);
    g.restore();
    if (sub) hand(sub, 0, ly + lh * 0.37, Math.min(Math.round(H * 0.055), UI.text(32)), t0, { color: C.leafDark, seed: seed + 6, dur: 0.4 });
    if (n != null) seal(-W / 2 + W * 0.2, -H / 2 + H * 0.2, W * 0.1, { text: String(n), seed: seed + 7, color });
    g.restore();
  }

  /* ---------------- greenhouse, vines, backdrop ---------------- */
  function greenhouse(x, y, w, h, { seed = 7 } = {}) {
    if (IMG.greenhouse) { cut('greenhouse', x, y + h / 2, h, { ax: 0.5, ay: 1, seed, depth: 1, boil: false }); return; }
    const W = R(w), H = R(h), wall = H * 0.62;
    // gable roof + glass walls on a white frame
    paperPiece(pts(`gable${W},${H}`, () => [[-W / 2 - 20, -H / 2 + (H - wall)], [0, -H / 2], [W / 2 + 20, -H / 2 + (H - wall)]]), x, y, C.frame, { seed, depth: 1.2, boil: false });
    paperPiece(pts(`gableG${W},${H}`, () => [[-W / 2 + 14, -H / 2 + (H - wall) - 6], [0, -H / 2 + 22], [W / 2 - 14, -H / 2 + (H - wall) - 6]]), x, y, C.glass, { seed: seed + 1, depth: 0, boil: false });
    paperRect(x, y + H / 2 - wall / 2, W, R(wall), C.frame, { seed: seed + 2, depth: 1.2, boil: false });
    paperRect(x, y + H / 2 - wall / 2, R(W - 28), R(wall - 28), C.glass, { seed: seed + 3, depth: 0, boil: false });
    const top = y + H / 2 - wall;
    for (let k = 1; k < 6; k++) { const xx = x - W / 2 + W * k / 6; ink([[xx, top + 10], [xx, y + H / 2 - 10]], { w: 6, color: C.frame, seed: seed + 10 + k, passes: 1, step: 60, amp: 0.3 }); }
    ink([[x - W / 2 + 10, top + wall * 0.45], [x + W / 2 - 10, top + wall * 0.45]], { w: 6, color: C.frame, seed: seed + 20, passes: 1, step: 60, amp: 0.3 });
    for (let k = 1; k < 4; k++) { const u = k / 4; ink([[x, y - H / 2 + 20], [x - W / 2 + W * u / 2 + 10, top - 4]], { w: 5, color: C.frame, seed: seed + 30 + k, passes: 1, step: 60, amp: 0.3 }); ink([[x, y - H / 2 + 20], [x + W / 2 - W * u / 2 - 10, top - 4]], { w: 5, color: C.frame, seed: seed + 40 + k, passes: 1, step: 60, amp: 0.3 }); }
    // light glints
    for (let k = 0; k < 4; k++) ink([[x - W * 0.35 + k * W * 0.2, top + wall * 0.15], [x - W * 0.3 + k * W * 0.2, top + wall * 0.05]], { w: 4, color: 'rgba(255,255,255,0.7)', seed: seed + 50 + k, passes: 1, step: 20 });
  }
  function vine(x, y, h, { seed = 11, side = 1, sway = 1, s = 1, img = true } = {}) {
    if (s <= 0.001) return;
    if (img && IMG.pea_plant) { cut('pea_plant', x, y, h, { ax: 0.5, ay: 1, seed, depth: 1, s, rot: Math.sin(T * 0.8 + seed) * 0.012 * sway, flip: side < 0 }); return; }
    g.save(); g.translate(x, y); g.scale(s, s);
    const r = rng(seed), stem = [];
    for (let i = 0; i <= 10; i++) { const u = i / 10; stem.push([Math.sin(u * 5 + seed) * 22 * side + side * u * 40, -u * h]); }
    ink(stem, { w: 5, color: C.leafDark, seed, passes: 1, step: 20, amp: 0.8 });
    for (let i = 1; i < 10; i++) {
      const [sx, sy] = stem[i], dir = i % 2 ? 1 : -1, sw = Math.sin(T * 1.1 + i + seed) * 0.05 * sway;
      paperPiece(pts('vleaf', () => [[0, 0], [34, -16], [58, 0], [34, 14]]), sx, sy, i % 3 ? C.leaf : C.greenPea, { seed: seed + i, depth: 0.5, sx: dir, rot: dir * (-0.4 + r() * 0.3) + sw });
      if (i % 3 === 0) {   // a tendril curl
        const cx = sx - dir * 30, cy = sy - 20, curl = [];
        for (let k = 0; k <= 14; k++) { const a = k / 14 * TAU * 1.4; curl.push([cx + Math.cos(a) * (14 - k * 0.8) * dir, cy + Math.sin(a) * (14 - k * 0.8)]); }
        ink([[sx, sy]].concat(curl), { w: 2, color: C.leafDark, seed: seed + 30 + i, passes: 1, step: 6, amp: 0.3 });
      }
      if (i === 4 || i === 7) pheno('pod', 'inflated', sx + dir * 44, sy + 26, 20, { seed: seed + 50 + i });
      if (i === 8) pheno('flower', 'violet', sx - dir * 36, sy - 10, 18, { seed: seed + 60 });
    }
    g.restore();
  }
  function backdrop(t0, { color = C.paper } = {}) {
    g.fillStyle = paperTile(color, 4); g.fillRect(0, 0, Game.W, Game.H);
    if (IMG.tex_paper) { g.save(); g.globalAlpha = 0.45; g.drawImage(IMG.tex_paper, 0, 0, Game.W, Game.H); g.restore(); }
    // a botanical plate's double rule
    g.save(); g.strokeStyle = 'rgba(80,60,35,0.45)'; g.lineWidth = 3; g.strokeRect(34, 34, Game.W - 68, Game.H - 68);
    g.lineWidth = 1.2; g.strokeRect(46, 46, Game.W - 92, Game.H - 92); g.restore();
    void t0;
  }

  window.Plate = { C, woodtype, woodWidth, label, twine, seal, tile, trayLayout, tray, pheno, PIC, packet, greenhouse, vine, backdrop };
})();
